import { LocalizationProvider } from '@progress/kendo-react-intl';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { CommonContextsProviders } from '../../../../common/contexts/CommonContextsProviders';
import { useMultiTabLogout } from '../../../../common/hooks/useMultiTabLogout';
import Loader from '../../../../common/widgets/Loader';
import LoadingSpinner from '../../../../common/widgets/LoadingSpinner';
import {
  logTrace,
  isTraceLogEnabled,
} from '../../../../common/telemetry/logTrace';
import { benefitUserManager } from '../../../../common/auth/oidcConfig';
import { renewTokenWithRetry } from '../../../../common/auth/renewTokenSilently';
import { config } from '../../config';
import { AuthenticatedBenefitContextsProviders } from '../../contexts/BenefitContextsProviders';
import { CustomerBudgetProvider } from '../../contexts/CustomerBudgetContext';
import {
  CustomerInfoContext,
  CustomerInfoProvider,
} from '../../contexts/CustomerInfoContext';
import { initializeI18n } from '../../resources/i18n';
import { Routing } from '../../Routing';
import { dynamicYieldService } from '../../services/dynamic-yield';
import { WithAcceptedTerms } from '../hocs/WithAcceptedTerms';
import { WithGlobalDialogs } from '../hocs/WithGlobalDialogs';
import { injectOneTrustScripts, loadKendoCustomMessages } from '../utils';

export function HealthyApp() {
  const auth = useAuth();
  const hasRedirected = useRef(false);
  // Impersonation must not reuse the previous user's refresh token.
  const requiresFreshSigninRef = useRef(
    new URLSearchParams(window.location.search).has('impersonate'),
  );
  useMultiTabLogout();

  useEffect(() => {
    logTrace('HealthyApp: mounted');
    return () => logTrace('HealthyApp: unmounted');
  }, []);

  // page lifecycle diagnostics: helps correlate auth failures with the device
  // going to sleep / waking up and with connectivity changes
  useEffect(() => {
    if (!isTraceLogEnabled) return;

    const documentEvents = ['visibilitychange', 'freeze', 'resume'];
    const windowEvents = ['pageshow', 'pagehide', 'online', 'offline'];
    const log = (e: Event) =>
      logTrace(`HealthyApp: page ${e.type}`, {
        persisted: (e as PageTransitionEvent).persisted,
      });

    documentEvents.forEach(e => document.addEventListener(e, log));
    windowEvents.forEach(e => globalThis.addEventListener(e, log));
    return () => {
      documentEvents.forEach(e => document.removeEventListener(e, log));
      windowEvents.forEach(e => globalThis.removeEventListener(e, log));
    };
  }, []);

  // always try to silently login once via auth code grant with PKCE or refresh token grant
  useEffect(() => {
    logTrace('HealthyApp: signin effect evaluated', {
      hasRedirected: hasRedirected.current,
      hasAuthParams: hasAuthParams(),
      isAuthenticated: auth.isAuthenticated,
      activeNavigator: auth.activeNavigator,
      isLoading: auth.isLoading,
      error: auth.error?.message,
    });

    if (
      !hasRedirected.current &&
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !auth.activeNavigator &&
      !auth.isLoading &&
      !auth.error
    ) {
      //do not save redirect as /impersonate, it will be handled by onSigninCallback later
      const url = new URL(window.location.href);
      if (url.searchParams.has('impersonate')) {
        url.searchParams.delete('impersonate');
      }

      window.sessionStorage.setItem(
        'redirectTo',
        `${url.pathname}${url.search}`,
      );

      hasRedirected.current = true;

      // The access token is short lived, so after a reload or a restored tab
      // (mobile browsers discard background tabs) the stored user is usually
      // expired and isAuthenticated is false, while its refresh token is still
      // valid. Use it first instead of a new /authorize round trip; only an
      // impersonation entry must go through a fresh signin.
      const canUseRefreshToken =
        !!auth.user?.refresh_token && !requiresFreshSigninRef.current;

      (async () => {
        if (canUseRefreshToken) {
          logTrace('HealthyApp: stored user expired, trying refresh token');
          const result = await renewTokenWithRetry('HealthyApp');
          if (result.user) {
            logTrace('HealthyApp: refresh token signin succeeded');
            return;
          }
          logTrace('HealthyApp: refresh token signin failed', {
            rejected: result.rejected,
            error: result.error,
          });

          // the IDP refused the refresh token (expired/revoked): the session is
          // over, so end it at the IDP instead of starting a new signin.
          // Its cookie may still be valid; this automatic logout needs no prompt.
          if (result.rejected) {
            logTrace(
              'HealthyApp: refresh token rejected, calling signoutRedirect',
              undefined,
              { flush: true },
            );
            try {
              await benefitUserManager.signoutRedirect({
                extraQueryParams: {
                  skip_logout_prompt: 'true',
                },
              });
              return;
            } catch (e) {
              logTrace('HealthyApp: signoutRedirect threw', {
                message: e instanceof Error ? e.message : String(e),
              });
              await benefitUserManager.removeUser();
            }
          }
        }

        logTrace(
          'HealthyApp: calling auth.signinRedirect()',
          {
            hasStoredUser: !!auth.user,
            hadRefreshToken: !!auth.user?.refresh_token,
            requiresFreshSignin: requiresFreshSigninRef.current,
          },
          { flush: true },
        );
        auth.signinRedirect();
      })();
    }
  }, [auth]);

  return (
    <Loader
      loading={!auth.isAuthenticated}
      loader={<LoadingSpinner size="large" />}
    >
      <AppComponent />
    </Loader>
  );
}

const AppComponent = () => {
  const auth = useAuth();
  const [userAccessToken, setUserAccessToken] = useState<string | undefined>(
    undefined,
  );
  const [isI18nReady, setIsI18nReady] = useState(false);
  const hasAuthRef = useRef(false);

  const initializeApp = async () => {
    await initializeI18n();
    setIsI18nReady(true);
  };

  useEffect(() => {
    if (!auth.isAuthenticated) return;

    if (auth.user?.access_token != userAccessToken) {
      if (userAccessToken) hasAuthRef.current = false;
      setUserAccessToken(auth.user?.access_token);
    }

    if (hasAuthRef.current) return;
    hasAuthRef.current = true;

    initializeApp();
  }, [auth.isAuthenticated, auth.user?.access_token, userAccessToken]);

  useEffect(() => {
    injectOneTrustScripts(true);
    return () => {};
  }, []);

  useEffect(() => {
    const handleForceSignout = async () => {
      await auth.signoutRedirect();
    };
    globalThis.addEventListener('force-signout', handleForceSignout);
    return () =>
      globalThis.removeEventListener('force-signout', handleForceSignout);
  }, [auth]);

  if (!isI18nReady) {
    return <LoadingSpinner size="large" />;
  }

  return (
    <CommonContextsProviders>
      <CustomerInfoProvider>
        <CustomerBudgetProvider>
          <LocalizedApp />
        </CustomerBudgetProvider>
      </CustomerInfoProvider>
    </CommonContextsProviders>
  );
};

const LocalizedApp = () => {
  const { t: translate } = useTranslation();
  const { customerInfo } = useContext(CustomerInfoContext);

  const currentLanguage = customerInfo?.languageCode ?? config.defaultLanguage;

  useEffect(() => {
    loadKendoCustomMessages(translate, customerInfo);
  }, [translate, customerInfo]);

  useEffect(() => {
    if (customerInfo?.allowsDy && customerInfo?.hasDynamicYieldConsent) {
      dynamicYieldService.setDyId();
    }
  }, [customerInfo?.allowsDy, customerInfo?.hasDynamicYieldConsent]);

  return (
    <LocalizationProvider language={currentLanguage ?? 'ro'}>
      <WithAcceptedTerms>
        <WithGlobalDialogs>
          <AuthenticatedBenefitContextsProviders>
            <Routing />
          </AuthenticatedBenefitContextsProviders>
        </WithGlobalDialogs>
      </WithAcceptedTerms>
    </LocalizationProvider>
  );
};
